import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNearbyCheckins } from '../hooks/useNearbyCheckins';
import { useEmberStore } from '../lib/store';
import { supabase, NearbyCheckIn, Event } from '../lib/supabase';
import MapMarkerCard from '../components/MapMarkerCard';
import EventPopup from '../components/EventPopup';
import LoadingSpinner from '../components/LoadingSpinner';
import BottomNav from '../components/BottomNav';
import DebugOverlay from '../components/DebugOverlay';
import { Navigation, AlertCircle, MapPin } from 'lucide-react';
import { useLeaflet } from '../hooks/useLeaflet';

export default function MapView() {
  const navigate = useNavigate();
  const [focusParams, setFocusParams] = useState<{ id?: string; lat?: number; lng?: number } | null>(null);
  const [focusedCheckinId, setFocusedCheckinId] = useState<string | null>(null);
  const { currentUser } = useEmberStore();
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [selectedCheckIn, setSelectedCheckIn] = useState<string | null>(null);
  const [nearbyEvents, setNearbyEvents] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const mapRef = useRef<any>(null);
  const hasInitialViewportSet = useRef(false);
  const [initialCenter, setInitialCenter] = useState<[number, number] | null>(null);
  // Overlay controls: show the nearby loading pill once per visit (unless reset by explicit recentre)
  const [hasShownNearbyPill, setHasShownNearbyPill] = useState(false);
  const [showNearbyPill, setShowNearbyPill] = useState(false);
  const nearbyPillTimerRef = useRef<number | null>(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    outdoorSeating: false,
    coveredSeating: false,
    heaters: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [pendingFilters, setPendingFilters] = useState({
    outdoorSeating: false,
    coveredSeating: false,
    heaters: false
  });
  const leaflet = useLeaflet();
  const L = leaflet?.L;
  const components = leaflet?.components;

  // Normalize default marker assets once per load
  useEffect(() => {
    if (!L) return;
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
  }, [L]);

  const pipeIcon = useMemo(() => {
    if (!L) return null;
    return new L.DivIcon({
      className: 'custom-pipe-marker',
      html: '<div style="background: #6baf4a; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; border: 2px solid #0b0b0b; box-shadow: 0 2px 6px rgba(107, 175, 74, 0.4);">🔥</div>',
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  }, [L]);

  const userPipeIcon = useMemo(() => {
    if (!L) return null;
    return new L.DivIcon({
      className: 'custom-user-pipe-marker',
      html: '<div style="background: #3fa0c9; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; border: 2px solid #0b0b0b; box-shadow: 0 2px 6px rgba(63, 160, 201, 0.4);">🔥</div>',
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });
  }, [L]);

  const focusedPipeIcon = useMemo(() => {
    if (!L) return null;
    return new L.DivIcon({
      className: 'custom-focused-pipe-marker',
      html: '<div style="background: #d3de5e; color: #0b0b0b; width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 800; border: 2px solid #0b0b0b; box-shadow: 0 2px 8px rgba(211, 222, 94, 0.6);">⬤</div>',
      iconSize: [26, 26],
      iconAnchor: [13, 13]
    });
  }, [L]);

  const eventIcon = useMemo(() => {
    if (!L) return null;
    return new L.DivIcon({
      className: 'custom-event-marker',
      html: '<div style="background: #3b82f6; color: white; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; border: 2px solid #0b0b0b; box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5);">📅</div>',
      iconSize: [28, 28],
      iconAnchor: [14, 14]
    });
  }, [L]);

  const userLocationIcon = useMemo(() => {
    if (!L) return null;
    return new L.DivIcon({
      className: 'custom-user-marker',
      html: '<div style="background: #3b82f6; color: white; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justifyContent: center; font-size: 10px; font-weight: bold; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">📍</div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10]
    });
  }, [L]);

  const { checkIns, loading, error, refetch } = useNearbyCheckins({
    latitude: userLocation?.lat,
    longitude: userLocation?.lng,
    radiusKm: currentUser?.event_notification_radius_km || 50,  // Use user's notification distance setting
    enabled: !!userLocation
  });

  // Filter check-ins based on amenities
  const filteredCheckIns = checkIns.filter(checkIn => {
    if (!filters.outdoorSeating && !filters.coveredSeating && !filters.heaters) {
      return true; // Show all if no filters active
    }
    
    const hasOutdoor = checkIn.outdoor_seating;
    const hasCovered = checkIn.covered_seating;
    const hasHeaters = checkIn.heaters;
    
    // Show if it matches any active filter
    return (filters.outdoorSeating && hasOutdoor) ||
           (filters.coveredSeating && hasCovered) ||
           (filters.heaters && hasHeaters);
  });

  // Filter functions
  const handleFilterChange = (filterName: keyof typeof filters) => {
    setPendingFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const clearAllFilters = () => {
    setPendingFilters({
      outdoorSeating: false,
      coveredSeating: false,
      heaters: false
    });
  };

  const setAllFilters = () => {
    setPendingFilters({
      outdoorSeating: true,
      coveredSeating: true,
      heaters: true
    });
  };

  const setRainyDayFilters = () => {
    setPendingFilters({
      outdoorSeating: false,
      coveredSeating: true,
      heaters: true
    });
  };

  // When opening filters, sync pending from applied filters
  useEffect(() => {
    if (showFilters) {
      setPendingFilters(filters);
    }
  }, [showFilters]);

  const applyFiltersAndRecentre = () => {
    setFilters(pendingFilters);
    setShowFilters(false);
    handleCenterOnLocation();
  };

  // Detect Firefox browser
  const isFirefox = () => {
    return navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  };

  // Get appropriate timeout based on browser
  const getGeolocationTimeout = () => {
    // Firefox needs longer timeout, especially on iOS
    return isFirefox() ? 18000 : 6000; // 18 seconds for Firefox, 6 seconds for others
  };

  // Get user-friendly error message based on error code
  const getErrorMessage = (error: GeolocationPositionError): string => {
    switch (error.code) {
      case 1: // PERMISSION_DENIED
        return 'Location permission denied. Please enable location access in your browser settings.';
      case 2: // POSITION_UNAVAILABLE
        return 'Location unavailable. Please check your GPS and network settings.';
      case 3: // TIMEOUT
        return isFirefox() 
          ? 'Location request timed out. Firefox may need more time. Please try again.'
          : 'Location request timed out. Please try again.';
      default:
        return 'Unable to get your location. Please enable location services.';
    }
  };

  // Function to get user's location (reusable for initial load and retry)
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by this browser');
      setLocationLoading(false);
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    // Use cached last-known location immediately if available (only on initial load)
    if (!userLocation) {
      try {
        const cached = localStorage.getItem('last_known_location');
        if (cached) {
          const { lat, lng } = JSON.parse(cached);
          if (typeof lat === 'number' && typeof lng === 'number') {
            setUserLocation({ lat, lng });
          }
        }
      } catch {}
    }

    // Stage 1: quick, low-accuracy attempt
    let settled = false;
    const timeout = getGeolocationTimeout();
    
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        settled = true;
        setLocationLoading(false);
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setUserLocation(loc);
        setLocationError(null);
        try { localStorage.setItem('last_known_location', JSON.stringify(loc)); } catch {}
        // Stage 2: upgrade via watchPosition (high accuracy)
        const watchId = navigator.geolocation.watchPosition(
          (p) => {
            const upgraded = { lat: p.coords.latitude, lng: p.coords.longitude };
            setUserLocation(upgraded);
            try { localStorage.setItem('last_known_location', JSON.stringify(upgraded)); } catch {}
          },
          () => {},
          { enableHighAccuracy: true, maximumAge: 0 }
        );
        // Stop watching after short time to save battery
        setTimeout(() => navigator.geolocation.clearWatch(watchId), 15000);
      },
      (err) => {
        if (settled) return;
        setLocationLoading(false);
        console.error('Geolocation error:', err);
        setLocationError(getErrorMessage(err));
      },
      { enableHighAccuracy: false, timeout, maximumAge: 15 * 60 * 1000 }
    );
  };

  // Retry function for "Try Again" button
  const handleRetryLocation = () => {
    getCurrentLocation();
  };

  // Get user's current location on initial mount
  useEffect(() => {
    getCurrentLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load events when user location is available or notification radius changes
  useEffect(() => {
    if (userLocation) {
      loadNearbyEvents();
    }
  }, [userLocation, currentUser?.event_notification_radius_km]);

  // Gentle, one-time nearby loading pill with anti-flicker delay
  useEffect(() => {
    if (loading && !hasShownNearbyPill) {
      // Start a short delay to avoid flashes
      nearbyPillTimerRef.current = window.setTimeout(() => setShowNearbyPill(true), 350) as unknown as number;
    } else if (!loading) {
      // Loading finished: clear timer and hide pill
      if (nearbyPillTimerRef.current) {
        clearTimeout(nearbyPillTimerRef.current);
        nearbyPillTimerRef.current = null;
      }
      if (showNearbyPill) {
        // Mark as shown so we don't show it again automatically
        setHasShownNearbyPill(true);
      }
      setShowNearbyPill(false);
    }
    return () => {
      if (nearbyPillTimerRef.current) {
        clearTimeout(nearbyPillTimerRef.current);
        nearbyPillTimerRef.current = null;
      }
    };
  }, [loading]);

  useEffect(() => {
    if (!initialCenter && userLocation) {
      setInitialCenter([userLocation.lat, userLocation.lng]);
    }
  }, [initialCenter, userLocation]);

  useEffect(() => {
    if (!mapRef.current || !userLocation) return;
    if (focusParams) return;
    if (hasInitialViewportSet.current) return;
    mapRef.current.setView([userLocation.lat, userLocation.lng], 13);
    hasInitialViewportSet.current = true;
  }, [userLocation, focusParams]);

  // Read query params for focusCheckin and coordinates
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('focusCheckin') || undefined;
    const lat = params.get('lat');
    const lng = params.get('lng');
    if (id && lat && lng) {
      const latNum = parseFloat(lat);
      const lngNum = parseFloat(lng);
      if (!Number.isNaN(latNum) && !Number.isNaN(lngNum)) {
        setFocusParams({ id, lat: latNum, lng: lngNum });
        setFocusedCheckinId(id);
        if (mapRef.current) {
          mapRef.current.setView([latNum, lngNum], 15);
          hasInitialViewportSet.current = true;
        }
        setInitialCenter([latNum, lngNum]);
      }
    }
  }, []);

  const handleCenterOnLocation = () => {
    setFocusParams(null);
    setFocusedCheckinId(null);
    if (userLocation && mapRef.current) {
      mapRef.current.setView([userLocation.lat, userLocation.lng], 15);
    }
    hasInitialViewportSet.current = true;
    // Allow the nearby pill again only on explicit user action
    setHasShownNearbyPill(false);
  };

  const handleViewCheckIn = (checkInId: string) => {
    navigate(`/checkins#checkin-${checkInId}`);
  };

  useEffect(() => {
    if (!mapRef.current || !focusParams?.lat || !focusParams?.lng) return;
    mapRef.current.setView([focusParams.lat, focusParams.lng], 15);
    hasInitialViewportSet.current = true;
  }, [focusParams]);

  // Open event details with a full event fetch to include photo_url and related fields
  async function handleViewEventDetails(summaryEvent: any) {
    try {
      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          creator:profiles(username),
          venue:venues(*),
          responses:event_responses(*)
        `)
        .eq('id', summaryEvent.id)
        .single();

      if (!error && data) {
        setSelectedEvent(data as any);
      } else {
        // Fallback to provided summary if query fails
        setSelectedEvent(summaryEvent);
      }
    } catch (err) {
      // Fallback on error
      setSelectedEvent(summaryEvent);
    }
  }

  // Load nearby events
  async function loadNearbyEvents() {
    if (!userLocation) return;
    
    setEventsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_nearby_events', {
        user_lat: userLocation.lat,
        user_lon: userLocation.lng,
        radius_km: currentUser?.event_notification_radius_km || 50  // Use user's notification distance setting
      });

      if (error) throw error;
      if (data) setNearbyEvents(data);
    } catch (err) {
      console.error('Error loading nearby events:', err);
    } finally {
      setEventsLoading(false);
    }
  }

  if (locationError) {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {locationLoading ? (
          <>
            <div className="spinner" style={{ marginBottom: '1rem' }}></div>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', textAlign: 'center' }}>
              Getting Your Location...
            </h2>
            <p style={{ color: '#999', textAlign: 'center', marginBottom: '2rem', maxWidth: '400px' }}>
              Please wait while we request your location. This may take a few moments.
            </p>
          </>
        ) : (
          <>
          <AlertCircle size={48} color="#e4574d" style={{ marginBottom: '1rem' }} />
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', textAlign: 'center' }}>
              Location Access Required
            </h2>
            <p style={{ color: '#999', textAlign: 'center', marginBottom: '2rem', maxWidth: '400px' }}>
              {locationError}
            </p>
            <button
              onClick={handleRetryLocation}
              disabled={locationLoading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: locationLoading ? '#666' : 'var(--color-accent)',
                color: '#0b0b0b',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: locationLoading ? 'not-allowed' : 'pointer',
                opacity: locationLoading ? 0.6 : 1
              }}
            >
              Try Again
            </button>
          </>
        )}
        <BottomNav />
      </div>
    );
  }

  if (!userLocation) {
    return (
      <div style={{ minHeight: '100vh', padding: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div className="spinner" style={{ marginBottom: '1rem' }}></div>
        <p style={{ color: '#999', textAlign: 'center' }}>
          Getting your location...
        </p>
        <BottomNav />
      </div>
    );
  }

  if (!leaflet || !L || !components) {
    return <LoadingSpinner fullPage text="Loading map..." />;
  }

  const { MapContainer, TileLayer, Marker, Popup, useMapEvents } = components;

  const ClearFocusOnMapClick = () => {
    useMapEvents({
      click: () => {
        setFocusedCheckinId(null);
      }
    });
    return null;
  };

  function UserLocationMarker({ lat, lng }: { lat: number; lng: number }) {
    return (
      <Marker position={[lat, lng]} icon={userLocationIcon ?? undefined}>
        <Popup>
          <div style={{ textAlign: 'center', padding: '0.5rem' }}>
            <strong>You are here</strong>
          </div>
        </Popup>
      </Marker>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      {/* Header */}
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid #1f1f1f',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        backgroundColor: '#0b0b0b',
        zIndex: 1000
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <MapPin size={24} color="var(--color-accent)" />
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600' }}>
            Local Pipe Smokers
          </h1>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              padding: '0.5rem',
              backgroundColor: showFilters ? 'var(--color-accent)' : 'var(--color-card)',
              border: '1px solid var(--color-accent)',
              borderRadius: '8px',
              color: showFilters ? '#0b0b0b' : 'var(--color-accent)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '600'
            }}
            title="Filter smoking spots"
            aria-label="Open filter options"
          >
            🗺️ Filters
          </button>
          
          <button
            onClick={handleCenterOnLocation}
            style={{
              padding: '0.5rem',
              backgroundColor: 'var(--color-card)',
              border: '1px solid var(--color-accent)',
              borderRadius: '8px',
              color: 'var(--color-accent)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            title="Center on my location"
            aria-label="Center map on my location"
          >
            <Navigation size={16} />
          </button>
        </div>
      </header>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{
          position: 'absolute',
          top: '80px',
          left: '1rem',
          right: '1rem',
          backgroundColor: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          borderRadius: '12px',
          padding: '1rem',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', margin: 0 }}>Filter Smoking Spots</h3>
            <button
              onClick={() => setShowFilters(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#999',
                cursor: 'pointer',
                fontSize: '1.25rem'
              }}
            >
              ×
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={pendingFilters.outdoorSeating}
                onChange={() => handleFilterChange('outdoorSeating')}
                style={{ margin: 0 }}
              />
              <span>🌞 Outdoor Seating</span>
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={pendingFilters.coveredSeating}
                onChange={() => handleFilterChange('coveredSeating')}
                style={{ margin: 0 }}
              />
              <span>🏠 Covered Seating</span>
            </label>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={pendingFilters.heaters}
                onChange={() => handleFilterChange('heaters')}
                style={{ margin: 0 }}
              />
              <span>🔥 Heaters</span>
            </label>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                color: 'var(--color-text)',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Show All
            </button>
            <button
              onClick={setAllFilters}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                color: 'var(--color-text)',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Show Only Amenities
            </button>
            <button
              onClick={setRainyDayFilters}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'var(--color-accent)',
                border: 'none',
                borderRadius: '6px',
                color: '#0b0b0b',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
            >
              🌧️ Rainy Day
            </button>
            <div style={{ flex: 1 }} />
            <button
              onClick={() => setShowFilters(false)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                border: '1px solid var(--color-border)',
                borderRadius: '6px',
                color: 'var(--color-text)',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
              aria-label="Cancel filter changes"
            >
              Cancel
            </button>
              <button
                onClick={applyFiltersAndRecentre}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--color-secondary)',
                  border: 'none',
                  borderRadius: '6px',
                  color: 'white',
                  cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600'
              }}
              aria-label="Apply filter changes"
            >
              Apply
            </button>
          </div>
          
          <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: '#999' }}>
            Showing {filteredCheckIns.length} of {checkIns.length} spots
          </div>
        </div>
      )}

      {/* Map Container */}
      <div style={{ height: 'calc(100vh - 140px)', position: 'relative' }}>
        <MapContainer
          center={initialCenter ?? [userLocation.lat, userLocation.lng]}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          zoomControl={false}
        >
          <ClearFocusOnMapClick />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <UserLocationMarker lat={userLocation.lat} lng={userLocation.lng} />
          
          {/* Nearby check-in markers */}
          {filteredCheckIns.map((checkIn) => {
            const isUserCheckIn = currentUser && checkIn.user_id === currentUser.id;
            const iconToUse =
              focusedCheckinId && focusedCheckinId === checkIn.id
                ? focusedPipeIcon
                : isUserCheckIn
                ? userPipeIcon
                : pipeIcon;
            return (
              <Marker
                key={checkIn.id}
                position={[checkIn.latitude, checkIn.longitude]}
                icon={iconToUse ?? undefined}
                eventHandlers={{
                  click: () => setFocusedCheckinId(checkIn.id)
                }}
              >
                <Popup maxWidth={350} maxHeight={400} className="custom-popup">
                  <MapMarkerCard
                    checkIn={checkIn}
                    onViewCheckIn={handleViewCheckIn}
                  />
                </Popup>
              </Marker>
            );
          })}

          {/* Event markers */}
          {nearbyEvents.map((event) => (
            <Marker
              key={event.id}
              position={[event.venue_latitude, event.venue_longitude]}
              icon={eventIcon ?? undefined}
            >
              <Popup maxWidth={350} maxHeight={400} className="custom-popup">
                <div style={{
                  background: '#1a1a1a',
                  border: '1px solid var(--color-secondary)',
                  borderRadius: '12px',
                  padding: '1rem',
                  color: 'white',
                  minWidth: '250px',
                  // Use event photo as background when available
                  ...(event.photo_url ? {
                    backgroundImage: `url(${event.photo_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {})
                }}>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: 'var(--color-secondary)' }}>
                    {event.title}
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#cfcfcf', marginBottom: '0.5rem', ...(event.photo_url ? { backgroundColor: 'rgba(0,0,0,0.45)', padding: '0.25rem 0.5rem', borderRadius: '6px' } : {}) }}>
                    📍 {event.venue_name}
                  </p>
                  <p style={{ fontSize: '0.875rem', color: '#d3de5e', marginBottom: '0.75rem', ...(event.photo_url ? { backgroundColor: 'rgba(0,0,0,0.45)', padding: '0.25rem 0.5rem', borderRadius: '6px' } : {}) }}>
                    📅 {new Date(event.start_datetime).toLocaleDateString()} at {new Date(event.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <button
                    onClick={() => handleViewEventDetails(event)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      background: 'var(--color-secondary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    View Event Details
                  </button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Loading overlay (first data load only, with anti-flicker) */}
        {showNearbyPill && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(11, 11, 11, 0.9)',
            padding: '1rem 2rem',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            zIndex: 1000
          }}>
            <LoadingSpinner size="small" text="Finding nearby smokers..." />
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div style={{
            position: 'absolute',
            top: '1rem',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(255, 68, 68, 0.9)',
            color: 'white',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontSize: '0.875rem',
            zIndex: 1000
          }}>
            {error}
          </div>
        )}

        {/* Check-in and event count */}
        {(filteredCheckIns.length > 0 || nearbyEvents.length > 0) && (
          <div style={{
            position: 'absolute',
            bottom: '5.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(11, 11, 11, 0.9)',
            color: 'var(--color-accent)',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontSize: '0.875rem',
            fontWeight: '600',
            zIndex: 1000,
            display: 'flex',
            gap: '1rem'
          }}>
            {filteredCheckIns.length > 0 && (
              <span>
                🔥 {filteredCheckIns.length} check-ins
                {filteredCheckIns.length !== checkIns.length && ` (${checkIns.length} total)`}
              </span>
            )}
            {nearbyEvents.length > 0 && <span>📅 {nearbyEvents.length} events</span>}
          </div>
        )}
      </div>

      {/* Event Popup Modal */}
      {selectedEvent && (
        <EventPopup
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onResponseUpdate={() => {
            setSelectedEvent(null);
            loadNearbyEvents();
          }}
        />
      )}

      <BottomNav />
      
      {/* Custom CSS for map markers */}
      <style>{`
        .custom-popup .leaflet-popup-content-wrapper {
          background: transparent;
          box-shadow: none;
          border-radius: 0;
        }
        
        .custom-popup .leaflet-popup-content {
          margin: 0;
          padding: 0;
        }
        
        .custom-pipe-marker {
          background: transparent;
          border: none;
        }
        
        .custom-user-marker {
          background: transparent;
          border: none;
        }
        
        .custom-event-marker {
          background: transparent;
          border: none;
        }

        .custom-focused-pipe-marker {
          background: transparent;
          border: none;
        }
      `}</style>
    </div>
  );
}
